import { type FieldError, isApiError, UnknownResultError } from '@saay/api-client';
import {
  formatLocalDate,
  localDateToSolarHijri,
  parseSolarHijriInput,
  toPersianDigits,
} from '@saay/formatting';
import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import { FieldSupportSlot, revealTruncatedLabel, supportDescribedBy } from './field-support.js';
import { type ApprovalPendingInfo, ApprovalPendingNotice } from './approval.js';
import { assignRef, NativeSelectPicker, useCompactPicker } from './picker-sheet.js';
import { Banner, Button } from './primitives.js';
import { ErrorState } from './states.js';

/**
 * Form pieces for React Hook Form screens (promoted from admin-web): a native select, a textarea, a
 * checkbox, a Solar Hijri date input, the server field-error mapping, and the command feedback
 * panel. They reuse the `saay-field` markup and classes, and React Hook Form's `register()` spreads
 * onto them.
 */

interface FrameProps {
  readonly children: (ids: {
    describedBy: string | undefined;
    id: string;
    invalid: boolean;
  }) => ReactNode;
  readonly error?: string | undefined;
  readonly hint?: ReactNode;
  readonly id?: string | undefined;
  readonly label: ReactNode;
}

function Frame({ children, error, hint, id, label }: FrameProps) {
  const generated = useId();
  const fieldId = id ?? generated;
  const { describedBy, errorId, hintId } = supportDescribedBy(fieldId, { error, hint });
  return (
    <div className="saay-field">
      <label className="saay-field__label" htmlFor={fieldId} onPointerEnter={revealTruncatedLabel}>
        {label}
      </label>
      {children({ describedBy, id: fieldId, invalid: error !== undefined })}
      <FieldSupportSlot error={error} errorId={errorId} hint={hint} hintId={hintId} />
    </div>
  );
}

export interface NativeSelectOption {
  readonly disabled?: boolean;
  readonly label: string;
  readonly value: string;
}

export interface NativeSelectProps extends Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  'id' | 'children'
> {
  readonly error?: string | undefined;
  readonly hint?: ReactNode;
  readonly id?: string;
  readonly label: ReactNode;
  readonly options: readonly NativeSelectOption[];
  /** The empty first option; omit to force a value. */
  readonly placeholder?: string;
}

/** A labelled native `<select>` (RTL, keyboard and screen-reader native) for forms and filters. */
export const NativeSelect = forwardRef<HTMLSelectElement, NativeSelectProps>(function NativeSelect(
  { error, hint, id, label, options, placeholder, ...rest },
  ref,
) {
  // Phones open the option picker (PickerSheet) instead of the platform's select popup.
  const compact = useCompactPicker();
  const selectRef = useRef<HTMLSelectElement | null>(null);
  return (
    <Frame error={error} hint={hint} id={id} label={label}>
      {({ describedBy, id: fieldId, invalid }) => (
        <div
          className="saay-field__control"
          data-invalid={invalid}
          data-picker={compact || undefined}
        >
          {compact ? (
            <NativeSelectPicker
              describedBy={describedBy}
              disabled={rest.disabled}
              id={fieldId}
              label={label}
              selectRef={selectRef}
            />
          ) : null}
          <select
            {...rest}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            {...(compact ? { 'aria-hidden': true, tabIndex: -1 } : {})}
            className="saay-select"
            id={compact ? `${fieldId}-native` : fieldId}
            ref={(node) => {
              selectRef.current = node;
              assignRef(ref, node);
            }}
          >
            {placeholder === undefined ? null : <option value="">{placeholder}</option>}
            {options.map((option) => (
              <option disabled={option.disabled} key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      )}
    </Frame>
  );
});

export type { TextAreaProps } from './text-fields.js';
export { TextArea } from './text-fields.js';

export type { CheckboxProps } from './selection.js';
export { Checkbox } from './selection.js';

function solarText(value: string): string {
  if (value === '') {
    return '';
  }
  try {
    const date = localDateToSolarHijri(value);
    return toPersianDigits(
      `${date.year}/${String(date.month).padStart(2, '0')}/${String(date.day).padStart(2, '0')}`,
    );
  } catch {
    return '';
  }
}

export interface SolarDateInputProps {
  readonly error?: string | undefined;
  readonly hint?: ReactNode;
  readonly id?: string;
  readonly label: ReactNode;
  readonly name?: string;
  readonly onBlur?: () => void;
  /** Called with a `LocalDate` (`YYYY-MM-DD`), or `''` while the text is empty or not a date. */
  readonly onChange: (value: string) => void;
  /** The current `LocalDate` value, or `''`. */
  readonly value: string;
}

/**
 * A Solar Hijri date typed as `۱۴۰۵/۰۷/۰۵` (Persian or Latin digits). It reports a `LocalDate`
 * converted calendar to calendar with no time-zone step (FTD §3.2), and echoes the long form.
 */
export function SolarDateInput({
  error,
  hint,
  id,
  label,
  name,
  onBlur,
  onChange,
  value,
}: SolarDateInputProps) {
  const [text, setText] = useState(() => solarText(value));
  const [parseError, setParseError] = useState<string | undefined>(undefined);

  useEffect(() => {
    // An outside reset (form reset, default) replaces the text; typing keeps it.
    if (value !== '' && parseSolarHijriInput(text) !== value) {
      setText(solarText(value));
    }
    if (value === '' && parseSolarHijriInput(text) !== null) {
      setText('');
    }
  }, [value]);

  const echo = value === '' ? undefined : formatLocalDate(value, 'long');

  return (
    <Frame
      error={parseError ?? error}
      hint={
        <>
          {echo === undefined ? 'به شکل سال/ماه/روز خورشیدی، مثل ۱۴۰۵/۰۷/۰۵' : echo}
          {hint === undefined ? null : <> — {hint}</>}
        </>
      }
      id={id}
      label={label}
    >
      {({ describedBy, id: fieldId, invalid }) => (
        <div className="saay-field__control" data-invalid={invalid}>
          <input
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            autoComplete="off"
            dir="ltr"
            id={fieldId}
            inputMode="numeric"
            name={name}
            onBlur={() => {
              setParseError(
                text.trim() !== '' && parseSolarHijriInput(text) === null
                  ? 'تاریخ خورشیدی معتبر نیست.'
                  : undefined,
              );
              onBlur?.();
            }}
            onChange={(event) => {
              const next = event.target.value;
              setText(next);
              const parsed = parseSolarHijriInput(next);
              if (parsed !== null) {
                setParseError(undefined);
              }
              onChange(parsed ?? '');
            }}
            placeholder="۱۴۰۵/۰۷/۰۵"
            value={text}
          />
        </div>
      )}
    </Frame>
  );
}

/** API field path (`plans[0].installmentCount`) → React Hook Form name (`plans.0.installmentCount`). */
export function toFormPath(field: string): string {
  return field.replace(/\[(\d+)\]/g, '.$1');
}

/**
 * Maps server `fieldErrors` onto the form's fields (FTD §11); the ones the form does not render are
 * returned, to be listed above the submit button.
 */
export function applyFieldErrors<Name extends string>(
  error: unknown,
  fields: readonly string[],
  /** React Hook Form's `setError` (or any setter of the same shape). */
  setError: (
    name: Name,
    error: { readonly message: string; readonly type: 'server' },
    options: { readonly shouldFocus: boolean },
  ) => void,
  /** Prepended to every API field path, for a form that nests several request bodies. */
  prefix = '',
): FieldError[] {
  if (!isApiError(error)) {
    return [];
  }
  const unmapped: FieldError[] = [];
  let focused = false;
  for (const fieldError of error.fieldErrors) {
    const name = `${prefix}${toFormPath(fieldError.field)}`;
    if (fields.includes(name)) {
      setError(
        name as Name,
        { message: fieldError.messageFa, type: 'server' },
        { shouldFocus: !focused },
      );
      focused = true;
    } else {
      unmapped.push(fieldError);
    }
  }
  return unmapped;
}

/** The server field errors that no rendered field shows, listed above the submit button. */
export function FieldErrorList({
  errors,
  labels = {},
}: {
  readonly errors: readonly FieldError[];
  readonly labels?: Readonly<Record<string, string>>;
}) {
  if (errors.length === 0) {
    return null;
  }
  return (
    <Banner tone="danger">
      <ul className="saay-error-list">
        {errors.map((fieldError) => (
          <li key={`${fieldError.field}:${fieldError.code}`}>
            {labels[fieldError.field] ?? labels[toFormPath(fieldError.field)] ?? null}
            {labels[fieldError.field] === undefined ? null : ': '}
            {fieldError.messageFa}
          </li>
        ))}
      </ul>
    </Banner>
  );
}

/** The state of a command as {@link CommandFeedback} reads it (the `@saay/web-app` command state fits). */
export interface CommandFeedbackState {
  /** The `202 ApprovalPending` body while `status` is `approval-pending` (AC §4.10). */
  readonly approval?: ApprovalPendingInfo | undefined;
  readonly error: unknown;
  readonly status: 'idle' | 'pending' | 'success' | 'approval-pending' | 'error' | 'unknown';
}

/**
 * The FTD §11 states of a command: pending (announced), unknown result with «بررسی وضعیت» (same
 * key), «برای تأیید ارسال شد» for a `202 ApprovalPending` (maker-checker, nothing applied yet), and
 * the server error with `messageFa` and the copyable request ID. Field errors are shown on the
 * fields, so a pure `REQUEST_INVALID` shows only its summary line here.
 */
export function CommandFeedback({
  approvalAction,
  onCheckStatus,
  pendingLabel = 'در حال ارسال…',
  state,
}: {
  /** Shown under the approval-pending notice, for example a link to the approval request. */
  readonly approvalAction?: ReactNode;
  readonly onCheckStatus: () => void;
  readonly pendingLabel?: string;
  readonly state: CommandFeedbackState;
}) {
  if (state.status === 'approval-pending' && state.approval !== undefined) {
    return <ApprovalPendingNotice action={approvalAction} approval={state.approval} />;
  }
  if (state.status === 'pending') {
    return (
      <p aria-live="polite" className="saay-pending" role="status">
        {pendingLabel}
      </p>
    );
  }
  if (state.status === 'unknown') {
    return (
      <section className="saay-panel" data-tone="warning" role="alert">
        <h2 className="saay-panel__title">نتیجه مشخص نیست</h2>
        <p className="saay-panel__text">
          {state.error instanceof UnknownResultError
            ? state.error.messageFa
            : 'نتیجه درخواست مشخص نیست.'}{' '}
          بررسی وضعیت همان درخواست را دوباره می‌فرستد و دوبار ثبت نمی‌شود.
        </p>
        <Button onClick={onCheckStatus} variant="secondary">
          بررسی وضعیت
        </Button>
      </section>
    );
  }
  if (state.status === 'error') {
    return <ErrorState error={state.error} onRetry={onCheckStatus} title="ثبت انجام نشد" />;
  }
  return null;
}
